import React from "react";
import { Link } from "react-router-dom";
import { LegalMarkdown } from "./LegalMarkdown";
import { PLATFORM_BRAND, PARENT_COMPANY } from "./config";
import { legalLinks, useLegalDoc, usePublicLegalContact } from "@/lib/legal";
import { useSharedSections } from "@/lib/useSharedSections";

const FACEBOOK_NOTICE = "This site is not a part of the Facebook™ website or Meta Platforms, Inc. Additionally, this site is not endorsed by Facebook™ in any way. FACEBOOK™ is a trademark of Meta Platforms, Inc.";

function PrivacyContact() {
  const c = usePublicLegalContact();
  if (!c || !(c.business_address || c.privacy_email || c.privacy_phone)) return null;
  return (
    <div className="lp-footer-copy" data-testid="lp-footer-privacy-contact">
      Privacy questions: {c.company}
      {c.business_address && <> · <span data-testid="lp-footer-privacy-address">{c.business_address}</span></>}
      {c.privacy_email && <> · <a href={`mailto:${c.privacy_email}`} data-testid="lp-footer-privacy-email">{c.privacy_email}</a></>}
      {c.privacy_phone && <> · <a href={`tel:${c.privacy_phone.replace(/[^0-9+]/g, "")}`} data-testid="lp-footer-privacy-phone">{c.privacy_phone}</a></>}
    </div>
  );
}

function FallbackDisclaimer({ isFirmBranded, displayBrand }) {
  return (
    <>
      <strong>ATTORNEY ADVERTISING.</strong> This is a paid attorney advertisement. Prior results do not
      guarantee a similar outcome. The information on this site is not legal advice.{" "}
      {isFirmBranded ? (
        <>This page is a marketing communication <strong>on behalf of {displayBrand}</strong>, operated by{" "}
          <strong>{PLATFORM_BRAND}</strong>, a marketing service of <strong>{PARENT_COMPANY}</strong>.{" "}</>
      ) : (
        <>{PLATFORM_BRAND} is a marketing service operated by <strong>{PARENT_COMPANY}</strong>.{" "}</>
      )}
      {PLATFORM_BRAND} is not a law firm and does not provide legal services. Not available in all states.
      Contacting us does not create an attorney-client relationship.
    </>
  );
}

function PolicyLink({ url, to, testId, children }) {
  if (url) return <a href={url} target="_blank" rel="noopener noreferrer" data-testid={testId}>{children}</a>;
  return <Link to={to} data-testid={testId}>{children}</Link>;
}

// Policy links only (new tab, so a visitor mid-quiz never loses their answers).
export function MiniLegal({ buyerCode, privacyUrl, termsUrl }) {
  const links = legalLinks(buyerCode);
  return (
    <div className="lp-mini-legal" data-testid="lp-mini-legal">
      <a href={privacyUrl || links.privacy} target="_blank" rel="noopener noreferrer" data-testid="lp-mini-privacy">Privacy Policy</a>
      <span aria-hidden="true">·</span>
      <a href={termsUrl || links.terms} target="_blank" rel="noopener noreferrer" data-testid="lp-mini-terms">Terms</a>
    </div>
  );
}

export function LegalFooter({ buyerCode, displayBrand, isFirmBranded, hidePhone, phone, privacyUrl, termsUrl }) {
  // Firm-branded pages without a buyer code keep the built-in firm wording.
  const code = isFirmBranded ? buyerCode : null;
  const { doc } = useLegalDoc("disclaimer", code);
  const useDoc = doc && (!isFirmBranded || code);
  const links = legalLinks(code);
  const owner = doc?.legal_name || displayBrand;
  const footerNote = useSharedSections().footer_note;
  return (
    <footer className="lp-footer">
      <div className="lp-footer-inner">
        <div className="lp-footer-legal" data-testid="lp-footer-legal">
          {useDoc
            ? <LegalMarkdown text={doc.markdown} testId="lp-footer-disclaimer" />
            : <FallbackDisclaimer isFirmBranded={isFirmBranded} displayBrand={displayBrand} />}
        </div>
        {footerNote && <div className="lp-footer-legal" style={{ marginTop: 8 }} data-testid="lp-footer-note">{footerNote}</div>}
        <div className="lp-footer-links">
          <PolicyLink url={privacyUrl} to={links.privacy} testId="lp-footer-privacy">Privacy Policy</PolicyLink>
          <PolicyLink url={termsUrl} to={links.terms} testId="lp-footer-terms">Terms of Service</PolicyLink>
          <Link to={links.partners} data-testid="lp-footer-partners">Legal Partners</Link>
          {/* Full page loads so each landing page gets its own TrustedForm certificate. */}
          <a href="/lp/auto-accident">Auto Accident</a>
          <a href="/lp/truck">Truck Accident</a>
          <a href="/lp/rideshare">Rideshare</a>
          <a href="/lp/motorcycle">Motorcycle</a>
          {!hidePhone && <a href={`tel:${phone.replace(/-/g, "")}`}>{phone}</a>}
        </div>
        <div className="lp-footer-copy" data-testid="lp-footer-copy">
          © {new Date().getFullYear()}{" "}
          {isFirmBranded
            ? <>{owner}. Marketing services by {PLATFORM_BRAND}, a {PARENT_COMPANY} company. All rights reserved.</>
            : <>{PLATFORM_BRAND}, a {PARENT_COMPANY} company. All rights reserved.</>}
          {" "}Do Not Sell or Share My Personal Information —{" "}
          <PolicyLink url={privacyUrl} to={links.privacy} testId="lp-footer-do-not-sell">exercise here</PolicyLink>.
        </div>
        <PrivacyContact />
        <div className="lp-footer-copy" data-testid="lp-footer-facebook">{FACEBOOK_NOTICE}</div>
      </div>
    </footer>
  );
}
